import { ApiRequests, ApiRequestsTypes } from '@tableau/data-catalog-client-components';
import { ServiceLocator } from '../app/ServiceLocator';
import { Formatters, Messages } from '../codegen/Localize';
import { assertUnreachable } from '../ts/helpers/TypescriptHelpers';
import { SiteRolesResource } from '../userManagement/services/resources/SiteRoles';
import { Tables } from './resources/Tables';
import { GetAllowedClassesRequest, IGetAllowedClassesParams, IGetAllowedClassesResult } from '../ts/ServerApi/requests/GetAllowedClasses';
import { GetDatabaseActionsRequest } from '../ts/ServerApi/requests/GetDatabaseActionsRequest';
import { GetDatasourceActionsRequest } from '../ts/ServerApi/requests/GetDatasourceActionsRequest';
import { GetFlowActionsRequest } from '../ts/ServerApi/requests/GetFlowActionsRequest';
import { GetTableActionsRequest } from '../ts/ServerApi/requests/GetTableActionsRequest';
import { GetWorkbookActionsRequest } from '../ts/ServerApi/requests/GetWorkbookActionsRequest';
import { Request } from '../httpClient/serverApi/requests/Request';
import { ISendImpactAnalysisEmailsParams, SendImpactAnalysisEmailsRequest } from '../ts/ServerApi/requests/SendImpactAnalysisEmailsRequest';
import { SetColumnDescriptionRequest } from '../ts/ServerApi/requests/SetColumnDescriptionRequest';
import { ISingleIdParams } from '../ts/ServerApi/types/actionParams';
import { IDatasourceActions } from '../ts/ServerApi/types/IDatasourceActions';
import { IEmptyResult } from '../httpClient/serverApi/types/IResponse';
import { TableAllowedActions } from '../ts/ServerApi/types/TableAllowedActions';
import { RestApiErrorResponse } from '../ts/services/RestApiService';
import { ToasterService, ToastType } from '../ts/services/ToasterService';
import { SearchSuggestionsService } from '../ts/services/SearchSuggestionsService';
import WebHttpClient from '../httpClient/WebHttpClient';
import { callViaApiService as setFieldDescriptionApi, EditFieldDescriptionResponse } from '../ts/restApi/EditFieldDescription';
import { callViaApiService as setDrillPathDescriptionApi, EditDrillPathFieldDescriptionResponse } from '../ts/restApi/EditDrillPathFieldDescription';
import { SessionSiteSelectors } from '../app/selectors/SessionInfoSelectors';
import { getAppStore } from '../app/AppStore';

type CatalogActionsRequestArgs = {
  contentType: 'database' | 'table' | 'flow' | 'workbook' | 'datasource';
  contentId: string;
};

interface CatalogPermissionsResponse {
  sendEmail: boolean;
}

function getContentActionsRequest(args: CatalogActionsRequestArgs): Request<ISingleIdParams, CatalogPermissionsResponse> {
  switch (args.contentType) {
    case 'database': return new GetDatabaseActionsRequest(args.contentId);
    case 'table': return new GetTableActionsRequest(args.contentId);
    case 'flow': return new GetFlowActionsRequest(args.contentId);
    case 'workbook': return new GetWorkbookActionsRequest(args.contentId);
    case 'datasource': return new GetDatasourceActionsRequest(args.contentId);
    default: assertUnreachable(args.contentType);
  }
}

async function searchMatchingTags(args: ApiRequestsTypes.SearchMatchingTagsArgs): Promise<ReadonlyArray<string>> {
  const SearchSuggestionsService = ServiceLocator.get<SearchSuggestionsService>('SearchSuggestionsService');
  const response = await SearchSuggestionsService.tags(args.partialText).getSlice(args.startIndex, args.maxItems);
  return response.items.map(t => t.name);
}

async function sendImpactAnalysisEmailAndShowToast(args: ISendImpactAnalysisEmailsParams): Promise<void> {
  return WebHttpClient.sendRequest(new SendImpactAnalysisEmailsRequest(args))
    .then((result: any) => {
      if (result && !result.errors) {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.dataCatalogEmailSuccess() });
      }

      if (!result || result.errors) {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
      }
    });
}

async function setColumnDescription(args: ApiRequestsTypes.SetColumnDescriptionArgs): Promise<void> {
  return WebHttpClient.sendRequest(new SetColumnDescriptionRequest(args.columnVizportalId, args.description))
    .then((result: IEmptyResult) => {
      if (result && !result.errors) {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
        return Promise.resolve();
      } else {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
        return Promise.reject();
      }
    });
}

async function setFieldDescription(args: ApiRequestsTypes.SetFieldDescriptionArgs): Promise<void> {
  return setFieldDescriptionApi(args)
    .then(
      // onfulfilled
      (result: EditFieldDescriptionResponse) => {
        if (result && 'httpErrorCode' in result) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
          return Promise.reject();
        } else {
          ToasterService.instance.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
          return Promise.resolve();
        }
      },
      // onrejected
      (reason) => {
        if (reason.status === 404 || reason.data.httpErrorCode === 404) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailureOn404() });
        } else {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
        }
        return Promise.reject(reason);
      }
    );
}

async function setDrillPathFieldDescription(args: ApiRequestsTypes.SetDrillPathFieldDescriptionArgs): Promise<void> {
  return setDrillPathDescriptionApi(args)
    .then(
      // onfulfilled
      (result: EditDrillPathFieldDescriptionResponse) => {
        if (result && 'httpErrorCode' in result) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
          return Promise.reject();
        } else {
          ToasterService.instance.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
          return Promise.resolve();
        }
      },
      // onrejected
      (reason) => {
        if (reason.status === 404 || reason.data.httpErrorCode === 404) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailureOn404() });
        } else {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
        }
        return Promise.reject(reason);
      }
    );
}

async function canUserSetColumnTags(args: ApiRequestsTypes.CanUserSetColumnTagsArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(new GetTableActionsRequest(args.tableVizportalId))
    .then((result: TableAllowedActions) => {
      if (result) {
        return Promise.resolve(result.setDescription);
      } else {
        return Promise.reject();
      }
    });
}

async function canUserSetColumnDescription(args: ApiRequestsTypes.CanUserSetColumnDescriptionArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(new GetTableActionsRequest(args.tableVizportalId))
    .then((result: TableAllowedActions) => {
      if (result) {
        return Promise.resolve(result.setDescription);
      } else {
        return Promise.reject();
      }
    });
}

async function canUserSetFieldDescription(args: ApiRequestsTypes.CanUserSetFieldDescriptionArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(new GetDatasourceActionsRequest(args.datasourceVizportalId))
  .then((result: IDatasourceActions) => {
    if (result) {
      return Promise.resolve(result.setDescription);
    } else {
      return Promise.reject();
    }
  });
}

async function canUserSendImpactAnalysisEmail(args: CatalogActionsRequestArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(getContentActionsRequest(args)).then(r => {
    if (r) {
      return Promise.resolve(r.sendEmail);
    } else {
      return Promise.reject();
    }
  });
}

async function getAllowedClasses(args: IGetAllowedClassesParams): Promise<IGetAllowedClassesResult> {
  return WebHttpClient.sendRequest(new GetAllowedClassesRequest(args));
}

function isSiteCreatorOrHigher(): boolean {
  const siteSessionRole = SessionSiteSelectors.siteRole(getAppStore().getState());
  if (siteSessionRole != null) {
    return SiteRolesResource.isSiteCreatorOrHigher(siteSessionRole);
  } else {
    return false;
  }
}

function updateColumnTags(params: ApiRequestsTypes.ColumnTagsParams): Promise<void> {
  const  { luids, tagsToAdd, tagsToRemove } = params;
  return Tables.changeTagsOnColumns({ columnLuids: luids, tagsToAdd, tagsToRemove })
    .then(() => {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.changeTagsColumnsSuccess({
          ITEM_COUNT: luids.length,
          ITEM_COUNT_LOC: Formatters.formatCount(luids.length),
        })
      });
    })
    .catch(e => {
      const errorResponse = e as RestApiErrorResponse;
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.changeTagsColumnsError({
          ITEM_COUNT: luids.length,
          ERROR_MESSAGE: errorResponse.data.error.detail,
        })
      });
      throw e;
    });
}

export const CatalogApiRequests: ApiRequests = {
  searchMatchingTags,
  sendImpactAnalysisEmailAndShowToast,
  setColumnDescription,
  setFieldDescription,
  setDrillPathFieldDescription,
  canUserSetColumnTags,
  canUserSetColumnDescription,
  canUserSetFieldDescription,
  canUserSendImpactAnalysisEmail,
  getAllowedClasses,
  isSiteCreatorOrHigher,
  updateColumnTags,
};
